import { Head, Link, router } from '@inertiajs/react'
import { useState, useCallback, useRef } from 'react'
import AppLayout from '@/layouts/AppLayout'
import Notification from '@/components/ui/Notification'
import { FormSelect, FormInput } from '@/components/ui/form'
import { Badge } from '@/components/catalyst/badge'
import { ArrowPathIcon, ArrowLeftIcon, TableCellsIcon, DocumentChartBarIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import ProductsTab, { type VpoProduct } from './ProductsTab'
import FinanceTab from './FinanceTab'
import ShipmentTab from './ShipmentTab'

// ─── Types ──────────────────────────────────────

interface Order {
  id: number
  vendor_id: number | null
  vendor_name: string | null
  user_id: number | null
  vendor_po_number: string | null
  customer_po_number: string | null
  ghost_link: string | null
  state: string | null
  vendor_po_status: string | null
  urgency: string | null
  new_season: string | null
  currency: string | null
  payment_term_id: number | null
  warehouse_id: number | null
  vendor_order_date: string | null
  vendor_po_start_ship_date: string | null
  vendor_po_cancel_date: string | null
  due_date_to_vendor: string | null
  vendor_invoice_date: string | null
  vendor_invoice_number: string | null
  vendor_invoice_checked: boolean | null
  product_total_amount: string | number | null
  commission_amount: string | number | null
  vendor_freight_cost: string | number | null
  created_at: string | null
}

interface SelectOption { id: number; name: string }

interface VpoShowProps {
  order: Order
  vendors: SelectOption[]
  warehouses: SelectOption[]
  payment_terms: SelectOption[]
  currencies: [string, string][]
  states: [string, string][]
  vendor_po_statuses: [string, string][]
  seasons: [string, string][]
  urgencies: [string, string][]
  products_count: number
  comments_count: number
  state_changes_count: number
  customer_orders_count: number
  tab: string

  // Finance tab props — totals up-front, payments + memos deferred.
  totals: {
    product_total: number; commission: number; freight: number
    total_invoice: number; received_cost: number
    sum_payments: number; sum_credit_memos: number; balance: number
    currency: string | null
  }
  companies: { id: number; name: string }[]
  payment_forms: { id: number; name: string }[]
  carriers: { id: number; name: string }[]

  // Defer-loaded — undefined on initial render, populated by Inertia after
  // the follow-up partial-reload.
  products?: VpoProduct[]
  payments?: {
    id: number; amount: number; date: string | null
    company_id: number | null; company_name: string | null
    form_of_payment_id: number | null; form_of_payment_name: string | null
  }[]
  credit_memos?: { id: number; amount: number; date: string | null }[]
  shipments?: {
    id: number; tracking_number: string | null
    carrier_id: number | null; carrier_name: string | null
    arrival_date: string | null; incoming_ship_date: string | null
    date_routed: string | null; count_pallets: number | null
    weight: string | null; freight_invoice_uploaded: boolean | null
    receiving_details: string | null
  }[]
}

// ─── State pill colors (mirrors Vpo/Index) ──────

const STATE_COLORS: Record<string, 'zinc' | 'indigo' | 'amber' | 'orange' | 'emerald' | 'red' | 'sky' | 'violet' | 'rose' | 'lime'> = {
  unconfirmed_vpo: 'amber', confirmed_vpo: 'sky', vendor_payments: 'violet',
  needs_routing: 'orange', shipped_from_brand: 'indigo', in_warehouse: 'emerald',
  scanned: 'lime', partially_scanned: 'amber', allocated: 'indigo',
  distributed: 'emerald', credit: 'rose', closed_win: 'emerald',
  closed_lost: 'zinc', vendor_po_issues: 'red',
}

function humanize(s: string | null) {
  if (!s) return '—'
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// ─── Small helpers (parallel CO Show ones) ──────

function DeferSkeleton({ label }: { label: string }) {
  return (
    <div data-testid={`defer-skeleton-${label}`} className="flex items-center gap-2 py-8 text-sm text-zinc-400">
      <ArrowPathIcon className="size-4 animate-spin" />
      Loading {label}…
    </div>
  )
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 items-center gap-3">
      <label className="text-sm text-zinc-600 dark:text-zinc-400">{label}</label>
      <div className="col-span-2">{children}</div>
    </div>
  )
}

function SelectInput({ name, value, options, onChange, placeholder }: {
  name: string
  value: string | number | null
  options: { value: string; label: string }[]
  onChange: (name: string, val: string) => void
  placeholder?: string
}) {
  const strValue = value != null ? String(value) : ''
  return (
    <FormSelect value={strValue} onChange={val => onChange(name, val)} options={options} placeholder={placeholder} />
  )
}

// ─── VpoShowActions — phase G + H reports row ───
// Three buttons that hit legacy endpoints:
//   1. Pivot Table  — POST /vpo/pivot_table with the VPO's CO IDs;
//      Rails streams an xlsx attachment.
//   2. Receiving Report — POST /vpo/receiving_report with vpo_id;
//      same xlsx download path.
//   3. Update From Receiving — multipart POST of an edited receiving
//      report xlsx back to /vpo/update_products/:id; vpo#update_products
//      pipes it through Interactions::Vpo::UpdateImport, which rewrites
//      product quantities in place.
function VpoShowActions({ vpoId, csrfToken }: { vpoId: number; csrfToken: string }) {
  const updateRef = useRef<HTMLInputElement>(null)
  const updateForm = useRef<HTMLFormElement>(null)

  return (
    <div className="flex items-center gap-2" data-testid="vpo-show-actions">
      {/* Pivot Table */}
      <form action="/vpo/pivot_table" method="POST" target="_blank" className="inline-flex">
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        {/* Empty selection → backend returns 0 orders, still emits a workbook */}
        <input type="hidden" name="settings[order_ids][]" value="" />
        <button
          type="submit"
          title="Download Pivot Table (xlsx)"
          aria-label="Pivot Table"
          data-testid="vpo-pivot-table"
          className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          <TableCellsIcon className="h-4 w-4" />
          Pivot Table
        </button>
      </form>

      {/* Receiving Report */}
      <form action="/vpo/receiving_report" method="POST" target="_blank" className="inline-flex">
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <input type="hidden" name="settings[vpo_id]" value={vpoId} />
        <button
          type="submit"
          title="Download Receiving Report (xlsx)"
          aria-label="Receiving Report"
          data-testid="vpo-receiving-report"
          className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          <DocumentChartBarIcon className="h-4 w-4" />
          Receiving Report
        </button>
      </form>

      {/* Update from Receiving Report (upload) */}
      <form
        ref={updateForm}
        action={`/vpo/update_products/${vpoId}`}
        method="POST"
        encType="multipart/form-data"
        className="inline-flex"
      >
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <input
          ref={updateRef}
          type="file"
          name="vpo_file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          data-testid="vpo-update-file-input"
          onChange={() => updateForm.current?.submit()}
        />
        <button
          type="button"
          title="Upload an edited receiving-report xlsx to update product quantities"
          aria-label="Update from Receiving Report"
          data-testid="vpo-update-from-receiving"
          onClick={() => updateRef.current?.click()}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
          Update from Receiving
        </button>
      </form>
    </div>
  )
}

// ─── TabNav ─────────────────────────────────────

const TABS = [
  { key: 'details',  label: 'Details' },
  { key: 'products', label: 'Products' },
  { key: 'finance',  label: 'Finance' },
  { key: 'shipment', label: 'Shipment' },
  { key: 'log',      label: 'Status Log' },
  { key: 'comments', label: 'Comments' },
] as const

function TabNav({ current, onTabChange, productsCount, commentsCount, logCount }: {
  current: string
  onTabChange: (t: string) => void
  productsCount: number
  commentsCount: number
  logCount: number
}) {
  const countFor = (key: string) =>
    key === 'products' ? productsCount :
    key === 'comments' ? commentsCount :
    key === 'log'      ? logCount      : 0

  return (
    <div className="border-b border-zinc-200 dark:border-zinc-700" data-testid="tab-nav">
      <nav className="flex gap-4 overflow-x-auto">
        {TABS.map(tab => {
          const c = countFor(tab.key)
          const active = current === tab.key
          return (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={clsx(
                'flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium',
                active
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
              )}
            >
              {tab.label}
              {c > 0 && (
                <span className={clsx(
                  'rounded-full px-1.5 text-[10px] font-semibold',
                  active ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300'
                         : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300'
                )}>{c}</span>
              )}
            </button>
          )
        })}
      </nav>
    </div>
  )
}

// ─── Component ──────────────────────────────────

export default function VpoShow({
  order, vendors, warehouses, payment_terms, currencies,
  states, vendor_po_statuses, seasons, urgencies,
  products_count, comments_count, state_changes_count, customer_orders_count, tab,
  products,
  totals, companies, payment_forms, carriers, payments, credit_memos, shipments,
}: VpoShowProps) {
  const [form, setForm] = useState({ ...order })
  const [activeTab, setActiveTabRaw] = useState(tab || 'details')
  const setActiveTab = useCallback((t: string) => {
    setActiveTabRaw(t)
    const url = new URL(window.location.href)
    url.searchParams.set('tab', t)
    window.history.replaceState({}, '', url.toString())
  }, [])

  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  const savedValues = useRef<Record<string, string>>({})
  const saveField = useCallback(async (name: string, value: string | unknown) => {
    const strVal = String(value ?? '')
    if (savedValues.current[name] === strVal) return
    // Legacy update route is POST /vpo/update/:id (no PATCH). It writes via
    // Interactions::Vpo::Update — params arrive as a `vpo` hash.
    try {
      const res = await fetch(`/vpo/update/${order.id}.json`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
        body:    JSON.stringify({ vpo: { [name]: value } }),
      })
      if (res.ok) {
        savedValues.current[name] = strVal
        setNotifMessage(`Saved:${humanize(name)}`)
        setShowNotif(true)
        router.reload({ only: ['order'] })
      }
    } catch { /* silent — Notification will not flash on transient failure */ }
  }, [order.id, csrfToken])

  const set = (name: string, value: string) => setForm(prev => ({ ...prev, [name]: value }))

  const setAndSave = (name: string, value: string) => {
    if (String(form[name as keyof typeof form] ?? '') === value) return
    set(name, value)
    saveField(name, value)
  }

  // ─── Render ───────────────────────────────────

  return (
    <>
      <Head title={form.vendor_po_number ? `VPO ${form.vendor_po_number}` : `VPO #${order.id}`} />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      {/* Header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-zinc-200 pb-3 dark:border-zinc-700">
        <div className="flex items-start gap-3">
          <Link href="/vpo/index" className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800" title="Back to list">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-zinc-900 dark:text-white" data-testid="vpo-show-title">
                {form.vendor_po_number || `VPO #${order.id}`}
              </h1>
              {form.state && (
                <Badge color={STATE_COLORS[form.state] || 'zinc'}>{humanize(form.state)}</Badge>
              )}
              {form.currency && (
                <Badge color="zinc">{form.currency.toUpperCase()}</Badge>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
              <span>{form.vendor_name || '—'}</span>
              {customer_orders_count > 0 && (
                <>
                  <span className="mx-2">•</span>
                  <span>{customer_orders_count} customer {customer_orders_count === 1 ? 'order' : 'orders'}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Reports + bulk-update actions (phases G + H). Native POST
            forms targeted at the legacy xlsx endpoints — file downloads
            land via Content-Disposition; the receiving-report uploader
            is a multipart submit that overwrites VPO products in place. */}
        <VpoShowActions vpoId={order.id} csrfToken={csrfToken} />
      </div>

      <TabNav
        current={activeTab}
        onTabChange={setActiveTab}
        productsCount={products_count}
        commentsCount={comments_count}
        logCount={state_changes_count}
      />

      <div className="mt-4">
        {/* ═══ DETAILS TAB ═══ */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="details-tab">
            <SectionCard title="Status">
              <Field label="State">
                <SelectInput name="state" value={form.state} onChange={setAndSave}
                  options={states.map(([k, v]) => ({ value: k, label: v }))} />
              </Field>
              <Field label="PO Status">
                <SelectInput name="vendor_po_status" value={form.vendor_po_status} onChange={setAndSave}
                  options={vendor_po_statuses.map(([k, v]) => ({ value: k, label: v }))} placeholder="None" />
              </Field>
              <Field label="Urgency">
                <SelectInput name="urgency" value={form.urgency} onChange={setAndSave}
                  options={urgencies.map(([k, v]) => ({ value: k, label: v }))} placeholder="None" />
              </Field>
              <Field label="Season">
                <SelectInput name="new_season" value={form.new_season} onChange={setAndSave}
                  options={seasons.map(([k, v]) => ({ value: k, label: v }))} placeholder="None" />
              </Field>
            </SectionCard>

            <SectionCard title="Order Info">
              <Field label="Vendor">
                <SelectInput name="vendor_id" value={form.vendor_id} onChange={setAndSave}
                  options={vendors.map(v => ({ value: String(v.id), label: v.name }))} placeholder="Select…" />
              </Field>
              <Field label="Warehouse">
                <SelectInput name="warehouse_id" value={form.warehouse_id} onChange={setAndSave}
                  options={warehouses.map(w => ({ value: String(w.id), label: w.name }))} placeholder="Select…" />
              </Field>
              <Field label="Payment Term">
                <SelectInput name="vendor_payment_term_id" value={form.payment_term_id} onChange={setAndSave}
                  options={payment_terms.map(t => ({ value: String(t.id), label: t.name }))} placeholder="Select…" />
              </Field>
              <Field label="Currency">
                <SelectInput name="currency" value={form.currency} onChange={setAndSave}
                  options={currencies.map(([k, v]) => ({ value: k, label: v }))} />
              </Field>
            </SectionCard>

            <SectionCard title="Numbers & Links">
              <Field label="Vendor PO #">
                <FormInput name="vendor_po_number" value={form.vendor_po_number ?? ''}
                  onChange={e => set('vendor_po_number', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('vendor_po', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Customer PO #">
                <FormInput name="customer_po_number" value={form.customer_po_number ?? ''}
                  onChange={e => set('customer_po_number', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('customer_po_number', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Ghost Link">
                <FormInput name="ghost_link" value={form.ghost_link ?? ''}
                  onChange={e => set('ghost_link', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('ghost_link', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Invoice #">
                <FormInput name="vendor_invoice_number" value={form.vendor_invoice_number ?? ''}
                  onChange={e => set('vendor_invoice_number', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('vendor_invoice_number', (e.target as HTMLInputElement).value)} />
              </Field>
            </SectionCard>

            <SectionCard title="Dates">
              <Field label="Order Date">
                <FormInput name="vendor_order_date" type="date" value={form.vendor_order_date}
                  onChange={e => set('vendor_order_date', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('vendor_order_date', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Start Ship">
                <FormInput name="vendor_po_start_ship_date" type="date" value={form.vendor_po_start_ship_date}
                  onChange={e => set('vendor_po_start_ship_date', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('vendor_po_start_ship_date', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Cancel Date">
                <FormInput name="vendor_po_cancel_date" type="date" value={form.vendor_po_cancel_date}
                  onChange={e => set('vendor_po_cancel_date', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('vendor_po_cancel_date', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Due to Vendor">
                <FormInput name="due_date_to_vendor" type="date" value={form.due_date_to_vendor}
                  onChange={e => set('due_date_to_vendor', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('due_date_to_vendor', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Invoice Date">
                <FormInput name="vendor_invoice_date" type="date" value={form.vendor_invoice_date}
                  onChange={e => set('vendor_invoice_date', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('vendor_invoice_date', (e.target as HTMLInputElement).value)} />
              </Field>
            </SectionCard>

            <SectionCard title="Totals">
              <Field label="Product Total">
                <FormInput name="product_total_amount" type="number" value={String(form.product_total_amount ?? '')}
                  onChange={e => set('product_total_amount', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('product_total_amount', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Commission">
                <FormInput name="commission_amount" type="number" value={String(form.commission_amount ?? '')}
                  onChange={e => set('commission_amount', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('commission_amount', (e.target as HTMLInputElement).value)} />
              </Field>
              <Field label="Freight Cost">
                <FormInput name="vendor_freight_cost" type="number" value={String(form.vendor_freight_cost ?? '')}
                  onChange={e => set('vendor_freight_cost', (e.target as HTMLInputElement).value)}
                  onBlur={e => saveField('vendor_freight_cost', (e.target as HTMLInputElement).value)} />
              </Field>
            </SectionCard>
          </div>
        )}

        {/* ═══ PRODUCTS TAB (phase C) ═══ */}
        {activeTab === 'products' && (
          products
            ? <ProductsTab products={products} currency={form.currency} />
            : <DeferSkeleton label="products" />
        )}

        {/* ═══ Skeleton tabs — phases D-E will fill ═══ */}
        {activeTab === 'finance' && (
          <FinanceTab
            vpoId={order.id}
            totals={totals}
            payments={payments}
            creditMemos={credit_memos}
            companies={companies}
            paymentForms={payment_forms}
            csrfToken={csrfToken}
            onNotify={(msg) => { setNotifMessage(msg); setShowNotif(true) }}
          />
        )}
        {activeTab === 'shipment' && (
          <ShipmentTab
            vpoId={order.id}
            shipments={shipments}
            carriers={carriers}
            csrfToken={csrfToken}
            onNotify={(msg) => { setNotifMessage(msg); setShowNotif(true) }}
          />
        )}
        {activeTab === 'log'      && <DeferSkeleton label="status log" />}
        {activeTab === 'comments' && <DeferSkeleton label="comments" />}
      </div>
    </>
  )
}

VpoShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
